import React, { useContext, useRef, useState } from 'react'
import {
  DialogTrigger,
  RangeCalendar,
  lightTheme,
  darkTheme,
  Provider as SpectrumProvider,
} from '@adobe/react-spectrum'
import { getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { usePress } from 'react-aria'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { ThemeContext } from '../../context/ThemeProvider'
import { useIsMobile } from '../../lib/hooks/useIsMobile'
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from '../../lib/utils/defaultDates'

type DateRangePickerProps = {
  filters: {
    dateRange: {
      startDate: string
      endDate: string
    }
  }
  onChange: (_: { dateRange: { startDate: string; endDate: string } }) => void
  collapsed?: boolean
  minValue?: string
}

const DateRangePicker = ({
  filters,
  onChange,
  collapsed,
  minValue,
}: DateRangePickerProps): JSX.Element => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const { theme } = useContext(ThemeContext)
  const isMobile = useIsMobile()

  const { pressProps } = usePress({
    onPress: () => {
      setOpen(true)
    },
  })

  const targetRef = useRef<HTMLDivElement>()

  return (
    <SpectrumProvider
      theme={theme === 'dark' ? darkTheme : lightTheme}
      colorScheme={theme}>
      <DialogTrigger
        /* @ts-ignore */
        targetRef={targetRef}
        isOpen={isOpen}
        onOpenChange={setOpen}
        type="popover"
        mobileType="tray"
        placement={'bottom right'}
        className={'bg-white dark:bg-slate-500 h-10'}>
        <motion.div
          layout={true}
          /* @ts-ignore */
          ref={targetRef}
          {...pressProps}
          className={`
            flex font-bold h-10 px-2 rounded-md gap-2 
            items-center cursor-pointer bg-white dark:bg-slate-500 overflow-x-hidden
            w-full max-w-[300px] xs:max-w-full
          `}>
          {!collapsed && (
            <motion.div
              layoutId={'calendar-icon'}
              layout={'preserve-aspect'}
              className="flex items-center gap-1">
              <span className={'text-xs truncate'}>
                {filters.dateRange.startDate === DEFAULT_START_DATE &&
                filters.dateRange.endDate === DEFAULT_END_DATE ? (
                  'All Dates'
                ) : (
                  <div>
                    {dayjs(filters.dateRange?.startDate)?.format(
                      isMobile ? 'M/D/YY' : 'MMM DD, YYYY',
                    )}{' '}
                    -{' '}
                    {dayjs(filters.dateRange?.endDate)?.format(
                      isMobile ? 'M/D/YY' : 'MMM DD, YYYY',
                    )}
                  </div>
                )}
              </span>
            </motion.div>
          )}
          <motion.div layout={'position'} className="ml-auto">
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </motion.div>
        </motion.div>
        <div className={' w-full flex flex-1 justify-center p-6 sm:p-8'}>
          <div className={'flex flex-col gap-4'}>
            <div className={' w-full overflow-x-auto'}>
              <RangeCalendar
                maxValue={today(getLocalTimeZone())}
                minValue={minValue ? parseDate(minValue) : null}
                visibleMonths={isMobile ? 1 : 2} // Reduced to 1 for smaller screens
                value={{
                  start: parseDate(filters.dateRange?.startDate),
                  end: parseDate(filters.dateRange?.endDate),
                }}
                onChange={(value) => {
                  if (!value) return
                  onChange({
                    dateRange: {
                      startDate: dayjs(value.start.toString()).format('YYYY-MM-DD'),
                      endDate: dayjs(value.end.toString()).format('YYYY-MM-DD'),
                    },
                  })
                  setOpen(false)
                }}
              />
            </div>
            <div
              className={'flex flex-row gap-4 items-center justify-center font-semibold'}>
              <div
                className={
                  'cursor-pointer text-sm p-2 rounded-lg bg-gray-200 dark:bg-slate-500'
                }
                onClick={() => {
                  onChange({
                    dateRange: {
                      startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
                      endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
                    },
                  })
                  setOpen(false)
                }}>
                This year
              </div>
              <div
                className={
                  ' cursor-pointer  text-sm p-2 rounded-lg bg-gray-200 dark:bg-slate-500'
                }
                onClick={() => {
                  onChange({
                    dateRange: {
                      startDate: dayjs()
                        .subtract(1, 'year')
                        .startOf('year')
                        .format('YYYY-MM-DD'),
                      endDate: dayjs()
                        .subtract(1, 'year')
                        .endOf('year')
                        .format('YYYY-MM-DD'),
                    },
                  })
                  setOpen(false)
                }}>
                Last year
              </div>
              <div
                className={
                  ' cursor-pointer  text-sm p-2 rounded-lg bg-gray-200 dark:bg-slate-500'
                }
                onClick={() => {
                  onChange({
                    dateRange: {
                      startDate: DEFAULT_START_DATE,
                      endDate: DEFAULT_END_DATE,
                    },
                  })
                  setOpen(false)
                }}>
                All Dates
              </div>
            </div>
          </div>
        </div>
      </DialogTrigger>
    </SpectrumProvider>
  )
}

export default DateRangePicker
